import debounce from 'lodash/debounce';
import { CancellationType } from 'gql/autogen/CancellationType';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import {
    getCrossedOutRate,
    mapActivityIdByRepresentativeInfo,
    mapActivityTokenByRepresentativeInfo,
    mapCancellationTypeByCancellation,
    mapPricingByPricingSummary,
    mapSupplierActivityCodeByRepresentativeInfo,
    selectDisplayPricing,
} from 'gql/service/graph/activityService';
import { getDetailUrl } from 'core/utils/activityUrlGenerator';
import getDurationText from 'core/utils/getDurationText';
import { getScrollPositionX, getScrollPositionY } from 'core/window/windowService';
import { measureFinishRender } from 'core/Performance/PerformanceService';
import { setItemInLocalStorage } from 'core-framework/window/storage';
import { LocalStorageKey } from 'core-framework/constant/StorageKeys';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCardContentClickEventActivitiesFieldsV12,
    ActivitiesCardContentHoverEventActivitiesFieldsV12,
    ActivitiesCardContentSeenEventActivitiesFieldsV12,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesCardContentClickEventActivitiesManifestV12,
    ActivitiesCardContentHoverEventActivitiesManifestV12,
    ActivitiesCardContentSeenEventActivitiesManifestV12,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { PartialActivitiesWebAppDataContextFields } from 'core/messaging/analytics/ActivitiesAppDataContext';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import { BadgeType } from 'gql/autogen/BadgeType';
import { Badges } from 'gql/autogen/Badges';
import { mapSelectedFiltersToAnalyticsField } from 'component/common/Filter/filter.utils';
import { FilterState } from 'component/common/Filter/Filter.types';
import { parseNumber } from 'core/utils/parseNumber';
import { ActivitiesPageTypes, isHomePage } from 'core/constant/analytics/AnalyticsPageType';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { createSelector } from '@reduxjs/toolkit';
import { selectConfigFromBootstrap, selectExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { isB, isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { ExperimentId } from 'core/type/ExperimentId';
import { ResultInfo } from 'gql/autogen/ResultInfo';

interface ActivityCardDispatchProps {
    onCardClick: () => void;
    onCardHover: () => void;
    onCardSeen: (inView: boolean) => void;
    measureImageLoadFirstCard: () => void;
}

export interface OwnProps {
    activity: ActivityCardProps;
    pageTypeId: ActivitiesPageTypes; // PageTypeId is used for traffic measurement
    isPopularActivitiesCard: boolean;
    keyword?: string;
    index: number;
    isSearchBoxFeatureGroup: boolean;
    elementName: string;
    shouldShowCityName: boolean;
}

export interface StateProps {
    config: BootstrapConfig;
    isBadgeV2Enabled: boolean;
    isRecentSearchFix: boolean;
    screenSize: ScreenSize;
    isAutoAppliedPricePeekEnabled: boolean;
}

export type Props = OwnProps & ActivityCardDispatchProps & StateProps;

const getBadgesWithoutFreeCancellation = (badges: Badges) => {
    const value = badges.activityHighlights?.filter((badge) => badge.badgeType !== BadgeType.FREE_CANCELLATION);
    return {
        ...badges,
        activityHighlights: value,
    };
};

export const activityCardMapper = (
    activities: ActivityWithContent[],
    config: BootstrapConfig,
    resultInfo?: ResultInfo,
    filters?: FilterState,
    travelDate?: Date,
    isKeywordSearch = false,
): ActivityCardProps[] => {
    if (!activities || activities.length === 0) {
        return [];
    }

    const cityIdFromBootstrap = isHomePage(config.pageConfig.pageTypeId) ? config.geo?.topDestinationCityId : config.geo?.cityId;
    const cms = config.cms;
    const language = config.language;
    const openDetailPageInNewTab = config.behavior.search.openDetailPageInNewTab;
    const priceBehavior = config.behavior.common.price;
    const hideFreeCancellation = config.behavior.common?.hideFreeCancellation;

    return activities.map((item: ActivityWithContent = {}, index): ActivityCardProps => {
        const activityContent = item.content || {};
        const activitySummary = activityContent.activity || {};
        const reviewSummary = activityContent && activityContent.reviewSummary;
        const averageScore = (reviewSummary && reviewSummary.averageScore) || '0';
        const averageScoreText = averageScore === '0' ? '0' : parseFloat(averageScore).toFixed(1);
        const durationText = activitySummary.duration ? getDurationText(activitySummary.duration, cms) : '';
        const pricingSummary = item.activityRepresentativeInfo?.pricingSummary;
        const activityId = mapActivityIdByRepresentativeInfo(item.activityRepresentativeInfo);
        const masterActivityId = item.masterActivityId || activityId;
        const activityToken = mapActivityTokenByRepresentativeInfo(item.activityRepresentativeInfo);
        const cancellationPolicy = mapCancellationTypeByCancellation(item.activityRepresentativeInfo?.cancellationPolicy);
        const supplierId = item.masterSupplierId;
        const supplierActivityCode = mapSupplierActivityCodeByRepresentativeInfo(item.activityRepresentativeInfo);
        const contentLocale = activityContent.contentLocale || 'en';
        const totalPrice = selectDisplayPricing(mapPricingByPricingSummary(item.activityRepresentativeInfo?.pricingSummary), priceBehavior.displayType);
        const hasCor = !!getCrossedOutRate(priceBehavior.crossedOutRateBehavior, totalPrice);
        const isHighlight = index === 0 && activityId > 0 && resultInfo?.selectedActivityId === activityId;
        const categories = activityContent.activity?.categories || [];
        const isWebView = config.isWebView;
        const filtersAnalyticsField = filters ? mapSelectedFiltersToAnalyticsField(filters) : '';
        const rankScore = item.rankScore;
        const badges = hideFreeCancellation ? activityContent.badges && getBadgesWithoutFreeCancellation(activityContent.badges) : activityContent.badges || {};
        const cityId = (isKeywordSearch ? activitySummary.location?.city?.id : cityIdFromBootstrap) || 0;
        return {
            index,
            pricingSummary,
            language,
            openDetailPageInNewTab,
            supplierId,
            supplierActivityCode,
            contentLocale,
            hasCor,
            totalPrice,
            isHighlight,
            categories,
            isWebView,
            filtersAnalyticsField,
            rankScore,
            badges,
            masterActivityId,
            cityId,
            pageTypeId: 0,
            id: activityId,
            token: activityToken,
            title: activitySummary.title || '',
            image: activityContent.images && activityContent.images[0],
            duration: durationText || '',
            rating: Math.round(parseFloat(averageScore) * 2) / 2,
            averageScore: averageScoreText,
            totalCount: reviewSummary?.totalCount || 0,
            cancellationPolicy: cancellationPolicy === CancellationType.FREE_CANCELLATION ? cms[146523] : '',
            url: getDetailUrl(activityId, language.cultureCode, cityId, travelDate),
            page_number: resultInfo?.pagination?.number || 0,
            cityName: activitySummary.location?.city?.name || '',
            countryName: activitySummary.location?.country?.name || '',
        };
    });
};

export const mapStateToProps = createSelector(selectConfigFromBootstrap, selectScreenSize, selectExperimentsFromBootstrap, (config, screenSize, experiments) => {
    return {
        config,
        screenSize,
        isBadgeV2Enabled: isOn(FeatureName.BadgesV2ActivityCard, config),
        isRecentSearchFix: isB(ExperimentId.ACT_4032, experiments),
        isAutoAppliedPricePeekEnabled: isB(ExperimentId.ACTB_AUTO_APPLIED_PRICE_PEEK, experiments),
    };
});

export const mapDispatchToProps = (_dispatch: Function, props: OwnProps): ActivityCardDispatchProps => {
    const { activity, pageTypeId, elementName } = props;

    const eventFields: ActivitiesCardContentClickEventActivitiesFieldsV12 = {
        is_highlight_activity: activity.isHighlight,
        page_number: activity.page_number,
        selected_filters: activity.filtersAnalyticsField,
        activities_categories: activity.categories?.join(',') || '',
        is_popular: props.isPopularActivitiesCard,
        search_phrase: props.keyword || '',
        is_free_cancellation: !!activity.badges?.activityHighlights?.find((badge) => badge.badgeType === BadgeType.FREE_CANCELLATION),
        final_price: activity.totalPrice?.allInclusive?.chargeTotal || 0,
        review_count: activity.totalCount,
        review_rating: parseNumber(activity.averageScore),
        action_element_index: activity.index + 1,
        action_element_value: elementName,
    };

    const appContextFields: PartialActivitiesWebAppDataContextFields = {
        activity_id: activity.id,
        supplier_id: activity.supplierId,
        has_crossout_rate: activity.hasCor,
        city_id: activity.cityId || 0,
    };

    return {
        measureImageLoadFirstCard: () => {
            if (activity.index === 0) {
                measureFinishRender({ component: 'ActivityCard' }, pageTypeId);
            }
        },
        onCardClick: () => {
            trackElementEvent<ActivitiesCardContentClickEventActivitiesFieldsV12>(ActivitiesCardContentClickEventActivitiesManifestV12.ids, eventFields, appContextFields);

            // Set Scroll Position
            const scrollPositionX = getScrollPositionX();
            const scrollPositionY = getScrollPositionY();

            const scrollData = JSON.stringify({ activityId: activity.id, x: scrollPositionX, y: scrollPositionY });
            setItemInLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY, scrollData);
        },
        onCardHover: debounce(() => {
            trackElementEvent<ActivitiesCardContentHoverEventActivitiesFieldsV12>(ActivitiesCardContentHoverEventActivitiesManifestV12.ids, eventFields, appContextFields);
        }, 500),
        onCardSeen: (inView: boolean) => {
            if (inView) {
                trackElementEvent<ActivitiesCardContentSeenEventActivitiesFieldsV12>(ActivitiesCardContentSeenEventActivitiesManifestV12.ids, eventFields, appContextFields);
            }
        },
    };
};
